import { mix, palette, type Palette } from './palette';

/**
 * Tiled backgrounds for the whole app.
 *
 * Every one is a small SVG drawn in line art and repeated — no image files, so
 * nothing to download and nothing to go blurry on a projector. They are drawn
 * at low opacity on purpose: a background you notice is a background you get
 * sick of by October, and this one sits behind a teacher's whole working day.
 *
 * The ids are stored in settings, so renaming a pattern is fine but changing
 * an id will quietly reset whoever had it chosen.
 */

export type BackgroundGroup =
  | 'Theme colours'
  | 'Plain'
  | 'School'
  | 'Flowers and nature'
  | 'Playful'
  | 'Autumn'
  | 'Winter'
  | 'Spring and summer'
  | 'Holidays';

export interface Background {
  id: string;
  name: string;
  group: BackgroundGroup;
  /** Tile size in pixels. Bigger tiles read as a pattern, smaller as texture. */
  size: number;
  /** The SVG body, drawn into a `size × size` tile. */
  body: string;
  /**
   * The colour of the page itself.
   *
   * Most backgrounds are a drawing over the app's own cream. These are the
   * ones that change the paper underneath, which is what a plain colour is.
   */
  color?: string;
  /** Ignore the shared opacity — textures set their own. */
  fullStrength?: boolean;
}

/* Shared drawing shorthands. Strokes are set on the group, not per path. */
const S = (d: string, w = 1.4) => `<path d="${d}" stroke-width="${w}"/>`;
const C = (cx: number, cy: number, r: number, w = 1.4) =>
  `<circle cx="${cx}" cy="${cy}" r="${r}" stroke-width="${w}"/>`;
const FILL = (d: string, fill: string) => `<path d="${d}" fill="${fill}" stroke="none"/>`;
const DOT = (cx: number, cy: number, r: number, fill: string) =>
  `<circle cx="${cx}" cy="${cy}" r="${r}" fill="${fill}" stroke="none"/>`;

/* A five-petal flower and a small leaf, reused across several patterns. */
const flower = (x: number, y: number, r: number, petal: string, heart: string) =>
  [0, 72, 144, 216, 288]
    .map(
      (a) =>
        `<ellipse cx="${x}" cy="${y - r}" rx="${r * 0.42}" ry="${r * 0.62}" fill="${petal}" stroke="none" transform="rotate(${a} ${x} ${y})"/>`,
    )
    .join('') + DOT(x, y, r * 0.3, heart);

const leaf = (x: number, y: number, s: number, color: string, rot = 0) =>
  `<path d="M${x} ${y}c0-${s} ${s * 0.8}-${s * 1.6} ${s * 1.6}-${s * 1.6} 0 ${s} -${s * 0.8} ${s * 1.6} -${s * 1.6} ${s * 1.6}z" fill="${color}" stroke="none" transform="rotate(${rot} ${x} ${y})"/>`;

/* Palette shorthands for the drawn patterns, which are fixed by design. */
const clay = '#C4A287';
const terracotta = '#B07C63';
const sage = '#A3B18A';
const olive = '#7C8A5F';
const rose = '#C08B8B';
const mauve = '#9C7A93';
const ochre = '#D9A85C';
const sand = '#DCCDBB';
const slate = '#8E9AAF';
const rust = '#9A6045';

/**
 * The whole Boho palette, as page colours.
 *
 * Each is mixed toward cream before it is used. A dashboard is read for seven
 * hours a day, and terracotta at full strength behind every card is a
 * different proposition from terracotta on a button — these want to be the
 * quiet thing the room is painted, not the loud thing in it.
 */
/*
 * The fourteen page colours, named by role rather than by hue.
 *
 * "Sage" is a green in Boho and a teal in Vivid, so a fixed colour name would
 * be wrong in nine themes out of ten. The swatch shows the colour; the label
 * only has to say what it is for.
 */
const PALETTE_WASH: { key: keyof Palette; name: string; wash: number }[] = [
  { key: 'cream', name: 'Lightest', wash: 1 },
  { key: 'linen', name: 'Light', wash: 1 },
  { key: 'sand', name: 'Soft', wash: 0.85 },
  { key: 'clay', name: 'Muted', wash: 0.68 },
  { key: 'ochre', name: 'Highlight', wash: 0.6 },
  { key: 'terracotta', name: 'Primary', wash: 0.55 },
  { key: 'rose', name: 'Blush', wash: 0.55 },
  { key: 'sage', name: 'Fresh', wash: 0.62 },
  { key: 'olive', name: 'Deep', wash: 0.55 },
  { key: 'mauve', name: 'Accent', wash: 0.55 },
  { key: 'stone', name: 'Neutral', wash: 0.6 },
  { key: 'rust', name: 'Strong', wash: 0.5 },
  { key: 'walnut', name: 'Dark', wash: 0.5 },
  { key: 'espresso', name: 'Darkest', wash: 0.45 },
];

/*
 * Each colour carries its own wash rather than one derived from brightness.
 *
 * A formula was tried first and produced three near-identical greys, because
 * stone, walnut and espresso all landed in the same band — fourteen options
 * of which three are the same colour is worse than eleven honest ones. These
 * are picked by eye so every one is distinguishable from its neighbours.
 */

/**
 * A plain wash of one palette colour.
 *
 * Computed from whichever theme is in force, so the fourteen page colours
 * are the theme's own — pick Jewel tone and these become emerald and garnet
 * rather than terracotta and sage.
 */
const plainColour = (c: (typeof PALETTE_WASH)[number], p: Palette): Background => ({
  // The id keeps its old prefix so a saved choice survives the rename.
  id: `boho-${c.key}`,
  name: c.name,
  group: 'Theme colours',
  size: 40,
  body: '',
  color: mix(p[c.key], p.cream, c.wash),
});

/**
 * The full list, for a given palette.
 *
 * The palette is a parameter rather than read from module state, and that is
 * the whole point: read ambiently, this is computed during render while the
 * new theme has only been written by an effect that has not run yet — so the
 * page colours would show the previous theme until something else caused a
 * repaint. Passing it in makes that impossible.
 */
export function backgrounds(p: Palette = palette()): Background[] {
  return [...PALETTE_WASH.map((c) => plainColour(c, p)), ...FIXED_BACKGROUNDS];
}

const FIXED_BACKGROUNDS: Background[] = [
  /* --------------------------------------------------------------- Plain */
  { id: 'none', name: 'Plain paper', group: 'Plain', size: 40, body: '' },
  {
    id: 'grid',
    name: 'Graph paper',
    group: 'Plain',
    size: 24,
    body: S('M24 0H0v24', 1),
  },
  {
    id: 'dots',
    name: 'Dot grid',
    group: 'Plain',
    size: 22,
    body: DOT(11, 11, 1.4, sand),
  },
  {
    id: 'lined',
    name: 'Ruled notebook',
    group: 'Plain',
    size: 26,
    body: S('M0 25h60', 1.2),
  },
  {
    id: 'weave',
    name: 'Linen weave',
    group: 'Plain',
    size: 16,
    body: S('M0 8h16M8 0v16', 0.9),
  },

  /* -------------------------------------------------------------- School */
  {
    id: 'pencils',
    name: 'Pencils',
    group: 'School',
    size: 76,
    body: [
      S('M12 58l3-11 22-22 8 8-22 22-11 3zM33 29l8 8', 1.5),
      S('M52 16l-3 11-11 11', 1.2),
      DOT(15, 55, 2, ochre),
    ].join(''),
  },
  {
    id: 'apples',
    name: 'Apples',
    group: 'School',
    size: 66,
    body: [
      `<path d="M33 24c-8 0-13 6-13 15s6 17 13 17 13-8 13-17-5-15-13-15z" fill="${rose}33" stroke="${rust}" stroke-width="1.4"/>`,
      S('M33 24v-6M33 18c3-3 7-3 9-1', 1.4),
      leaf(36, 16, 5, `${olive}55`, -20),
    ].join(''),
  },
  {
    id: 'crayons',
    name: 'Crayon box',
    group: 'School',
    size: 64,
    body: [
      `<path d="M12 52V26l5-9 5 9v26z" fill="${terracotta}22" stroke="${terracotta}" stroke-width="1.3"/>`,
      `<path d="M32 52V30l5-9 5 9v22z" fill="${ochre}22" stroke="${ochre}" stroke-width="1.3"/>`,
      `<path d="M50 52V34l4-7 4 7v18z" fill="${sage}22" stroke="${olive}" stroke-width="1.3"/>`,
    ].join(''),
  },
  {
    id: 'abc',
    name: 'ABC and 123',
    group: 'School',
    size: 84,
    body: `<text x="8" y="30" font-family="Georgia,serif" font-size="22" fill="${clay}55" stroke="none">A</text>
           <text x="44" y="34" font-family="Georgia,serif" font-size="20" fill="${sage}66" stroke="none">b</text>
           <text x="12" y="70" font-family="Georgia,serif" font-size="20" fill="${rose}66" stroke="none">1</text>
           <text x="46" y="72" font-family="Georgia,serif" font-size="22" fill="${ochre}66" stroke="none">2</text>`,
  },
  {
    id: 'stars-gold',
    name: 'Gold stars',
    group: 'School',
    size: 58,
    body: [
      FILL('M29 12l4 8 9 1.4-6.5 6.3 1.6 9-8.1-4.3-8.1 4.3 1.6-9L16 21.4l9-1.4z', `${ochre}44`),
      S('M29 12l4 8 9 1.4-6.5 6.3 1.6 9-8.1-4.3-8.1 4.3 1.6-9L16 21.4l9-1.4z', 1.2),
    ].join(''),
  },
  {
    id: 'books',
    name: 'Stacked books',
    group: 'School',
    size: 72,
    body: [
      `<rect x="14" y="38" width="42" height="8" rx="1.5" fill="${sage}22" stroke="${olive}" stroke-width="1.2"/>`,
      `<rect x="18" y="46" width="38" height="8" rx="1.5" fill="${rose}22" stroke="${rust}" stroke-width="1.2"/>`,
      `<rect x="12" y="54" width="46" height="8" rx="1.5" fill="${ochre}22" stroke="${clay}" stroke-width="1.2"/>`,
      S('M20 42h30M24 50h26M18 58h34', 0.8),
    ].join(''),
  },
  {
    id: 'schoolbus',
    name: 'School bus',
    group: 'School',
    size: 92,
    body: [
      `<rect x="16" y="30" width="60" height="30" rx="5" fill="${ochre}22" stroke="${clay}" stroke-width="1.4"/>`,
      S('M16 42h60M34 30v12M52 30v12', 1),
      C(30, 62, 5),
      C(62, 62, 5),
    ].join(''),
  },
  {
    id: 'clips',
    name: 'Paper clips',
    group: 'School',
    size: 60,
    body: S('M22 14v22a8 8 0 0 0 16 0V18a5 5 0 0 0-10 0v18a2 2 0 0 0 4 0V20', 1.4),
  },
  {
    id: 'chalk',
    name: 'Chalkboard swirls',
    group: 'School',
    size: 80,
    body: S('M6 40c10-16 24-16 34 0s24 16 34 0M6 62c10-10 24-10 34 0s24 10 34 0', 1.2),
  },

  /* --------------------------------------------------- Flowers and nature */
  {
    id: 'daisies',
    name: 'Daisies',
    group: 'Flowers and nature',
    size: 62,
    body:
      flower(31, 24, 11, `${rose}40`, `${ochre}66`) + flower(8, 52, 7, `${mauve}33`, `${ochre}55`),
  },
  {
    id: 'wildflowers',
    name: 'Wildflowers',
    group: 'Flowers and nature',
    size: 74,
    body: [
      S('M20 66V38M20 46c-6-2-8-8-6-12M20 50c6-2 9-7 8-12', 1.2),
      flower(20, 34, 7, `${mauve}40`, `${ochre}66`),
      S('M52 66V44M52 52c5-2 7-6 6-10', 1.2),
      flower(52, 40, 6, `${rose}40`, `${ochre}66`),
    ].join(''),
  },
  {
    id: 'eucalyptus',
    name: 'Eucalyptus',
    group: 'Flowers and nature',
    size: 72,
    body: [
      S('M6 66C22 50 34 32 40 8', 1.2),
      leaf(18, 50, 6, `${sage}44`, 40),
      leaf(26, 40, 6, `${sage}44`, -40),
      leaf(32, 28, 6, `${olive}33`, 40),
      leaf(38, 16, 6, `${sage}44`, -40),
    ].join(''),
  },
  {
    id: 'ferns',
    name: 'Ferns',
    group: 'Flowers and nature',
    size: 68,
    body: S(
      'M34 64V10M34 54c-8-1-12-6-13-12M34 54c8-1 12-6 13-12M34 42c-7-1-10-5-11-10M34 42c7-1 10-5 11-10M34 30c-6-1-8-4-9-8M34 30c6-1 8-4 9-8',
      1.1,
    ),
  },
  {
    id: 'sunbursts',
    name: 'Little suns',
    group: 'Flowers and nature',
    size: 64,
    body: [
      `<circle cx="32" cy="30" r="9" fill="${ochre}30" stroke="${ochre}" stroke-width="1.3"/>`,
      S(
        'M32 15v-5M32 50v5M17 30h-5M47 30h5M21 19l-3.5-3.5M43 41l3.5 3.5M43 19l3.5-3.5M21 41l-3.5 3.5',
        1.2,
      ),
    ].join(''),
  },
  {
    id: 'butterflies',
    name: 'Butterflies',
    group: 'Flowers and nature',
    size: 70,
    body: [
      S('M35 20v26', 1.2),
      `<path d="M35 24c-3-7-13-9-15-3s5 14 15 11" fill="${mauve}26" stroke="${mauve}" stroke-width="1.2"/>`,
      `<path d="M35 24c3-7 13-9 15-3s-5 14-15 11" fill="${mauve}26" stroke="${mauve}" stroke-width="1.2"/>`,
      `<path d="M35 32c-3 7-11 10-13 4s4-10 13-8" fill="${rose}26" stroke="${rose}" stroke-width="1.2"/>`,
      `<path d="M35 32c3 7 11 10 13 4s-4-10-13-8" fill="${rose}26" stroke="${rose}" stroke-width="1.2"/>`,
    ].join(''),
  },
  {
    id: 'bees',
    name: 'Bees and clover',
    group: 'Flowers and nature',
    size: 66,
    body: [
      `<ellipse cx="26" cy="26" rx="9" ry="6.5" fill="${ochre}3A" stroke="${rust}" stroke-width="1.2"/>`,
      S('M23 20.5v11M28 20v12', 1.2),
      `<ellipse cx="20" cy="17" rx="7" ry="3.5" fill="${slate}22" stroke="${slate}" stroke-width="1" transform="rotate(-25 20 17)"/>`,
      `<ellipse cx="31" cy="16" rx="7" ry="3.5" fill="${slate}22" stroke="${slate}" stroke-width="1" transform="rotate(20 31 16)"/>`,
      S('M35 24c3-1 4-3 3-5', 1),
      flower(50, 50, 7, `${sage}3A`, `${olive}55`),
    ].join(''),
  },
  {
    id: 'rainbows',
    name: 'Rainbows',
    group: 'Flowers and nature',
    size: 78,
    body: [
      `<path d="M10 58a29 29 0 0 1 58 0" fill="none" stroke="${rose}" stroke-width="2.4"/>`,
      `<path d="M17 58a22 22 0 0 1 44 0" fill="none" stroke="${ochre}" stroke-width="2.4"/>`,
      `<path d="M24 58a15 15 0 0 1 30 0" fill="none" stroke="${sage}" stroke-width="2.4"/>`,
      `<path d="M31 58a8 8 0 0 1 16 0" fill="none" stroke="${slate}" stroke-width="2.4"/>`,
    ].join(''),
  },

  /* ------------------------------------------------------------- Playful */
  {
    id: 'confetti',
    name: 'Confetti',
    group: 'Playful',
    size: 54,
    body: [
      `<rect x="8" y="10" width="7" height="3" rx="1.5" fill="${rose}55" stroke="none" transform="rotate(25 11 11)"/>`,
      `<rect x="34" y="18" width="7" height="3" rx="1.5" fill="${ochre}55" stroke="none" transform="rotate(-40 37 19)"/>`,
      `<rect x="16" y="36" width="7" height="3" rx="1.5" fill="${sage}66" stroke="none" transform="rotate(60 19 37)"/>`,
      `<rect x="38" y="42" width="7" height="3" rx="1.5" fill="${mauve}55" stroke="none" transform="rotate(-15 41 43)"/>`,
    ].join(''),
  },
  {
    id: 'balloons',
    name: 'Balloons',
    group: 'Playful',
    size: 76,
    body: [
      `<ellipse cx="24" cy="24" rx="10" ry="12" fill="${rose}2E" stroke="${rose}" stroke-width="1.3"/>`,
      S('M24 36c0 8 6 8 6 16', 1.1),
      `<ellipse cx="54" cy="40" rx="8" ry="10" fill="${slate}2E" stroke="${slate}" stroke-width="1.3"/>`,
      S('M54 50c0 7-5 7-5 14', 1.1),
    ].join(''),
  },
  {
    id: 'kites',
    name: 'Kites',
    group: 'Playful',
    size: 80,
    body: [
      `<path d="M34 10l16 16-16 16-16-16z" fill="${slate}26" stroke="${slate}" stroke-width="1.3"/>`,
      S('M34 10v32M18 26h32M34 42c0 8-6 8-6 16', 1.1),
    ].join(''),
  },
  {
    id: 'hearts',
    name: 'Little hearts',
    group: 'Playful',
    size: 52,
    body: [
      FILL(
        'M26 36S12 27 12 19.5A6.5 6.5 0 0 1 26 15a6.5 6.5 0 0 1 14 4.5C40 27 26 36 26 36z',
        `${rose}30`,
      ),
      S('M26 36S12 27 12 19.5A6.5 6.5 0 0 1 26 15a6.5 6.5 0 0 1 14 4.5C40 27 26 36 26 36z', 1.2),
    ].join(''),
  },
  {
    id: 'clouds',
    name: 'Soft clouds',
    group: 'Playful',
    size: 88,
    body: [
      `<path d="M18 50h32a10 10 0 0 0 1-20 13 13 0 0 0-25 3A8 8 0 0 0 18 50z" fill="${slate}1F" stroke="${slate}" stroke-width="1.2"/>`,
      `<path d="M52 74h22a7 7 0 0 0 .5-14 9 9 0 0 0-17 2A5.5 5.5 0 0 0 52 74z" fill="${slate}18" stroke="${slate}" stroke-width="1"/>`,
    ].join(''),
  },
  {
    id: 'paws',
    name: 'Paw prints',
    group: 'Playful',
    size: 60,
    body: [
      DOT(30, 32, 6.5, `${clay}44`),
      DOT(22, 22, 3, `${clay}44`),
      DOT(30, 19, 3, `${clay}44`),
      DOT(38, 22, 3, `${clay}44`),
    ].join(''),
  },

  /* -------------------------------------------------------------- Autumn */
  {
    id: 'fall-leaves',
    name: 'Falling leaves',
    group: 'Autumn',
    size: 76,
    body: [
      leaf(18, 20, 9, `${rust}33`, 20),
      leaf(52, 34, 8, `${ochre}3A`, -35),
      leaf(30, 58, 8, `${terracotta}33`, 65),
      S('M18 20v12M52 34v11M30 58v10', 1),
    ].join(''),
  },
  {
    id: 'acorns',
    name: 'Acorns',
    group: 'Autumn',
    size: 64,
    body: [
      `<path d="M22 30h20a10 10 0 0 1-10 20 10 10 0 0 1-10-20z" fill="${clay}30" stroke="${rust}" stroke-width="1.3"/>`,
      `<path d="M20 30a12 5 0 0 1 24 0z" fill="${rust}33" stroke="${rust}" stroke-width="1.3"/>`,
      S('M32 24v-6', 1.2),
    ].join(''),
  },
  {
    id: 'pumpkins',
    name: 'Pumpkins',
    group: 'Autumn',
    size: 72,
    body: [
      `<ellipse cx="36" cy="44" rx="18" ry="14" fill="${ochre}2E" stroke="${terracotta}" stroke-width="1.4"/>`,
      S('M28 32c-4 8-4 16 0 24M44 32c4 8 4 16 0 24M36 30v28', 1.1),
      S('M36 30c0-5 4-7 8-6', 1.2),
    ].join(''),
  },
  {
    id: 'thanksgiving',
    name: 'Harvest and wheat',
    group: 'Autumn',
    size: 78,
    body: [
      S('M38 70V26', 1.2),
      // Wheat: fat grains on a stalk, not the thin arcs a fern makes — the
      // two patterns sat next to each other in the picker and looked alike.
      `<ellipse cx="38" cy="20" rx="3.2" ry="6" fill="${ochre}3A" stroke="${clay}" stroke-width="1"/>`,
      `<ellipse cx="31" cy="28" rx="3" ry="5.5" fill="${ochre}33" stroke="${clay}" stroke-width="1" transform="rotate(-30 31 28)"/>`,
      `<ellipse cx="45" cy="28" rx="3" ry="5.5" fill="${ochre}33" stroke="${clay}" stroke-width="1" transform="rotate(30 45 28)"/>`,
      `<ellipse cx="31" cy="40" rx="3" ry="5.5" fill="${ochre}33" stroke="${clay}" stroke-width="1" transform="rotate(-30 31 40)"/>`,
      `<ellipse cx="45" cy="40" rx="3" ry="5.5" fill="${ochre}33" stroke="${clay}" stroke-width="1" transform="rotate(30 45 40)"/>`,
      S('M38 34l-6-4M38 34l6-4M38 46l-6-4M38 46l6-4', 1),
      leaf(12, 60, 7, `${rust}2E`, 30),
    ].join(''),
  },
  {
    id: 'halloween',
    name: 'Bats and moons',
    group: 'Autumn',
    size: 82,
    body: [
      `<path d="M20 30c4-6 8-2 10 2 2-4 6-8 10-2-3 0-4 3-4 6-3-3-9-3-12 0 0-3-1-6-4-6z" fill="${mauve}33" stroke="${mauve}" stroke-width="1.2"/>`,
      `<path d="M64 60a10 10 0 0 1-12-12 10 10 0 1 0 12 12z" fill="${ochre}2E" stroke="${clay}" stroke-width="1.2"/>`,
      DOT(16, 62, 1.6, `${ochre}66`),
      DOT(46, 20, 1.4, `${ochre}55`),
    ].join(''),
  },

  /* -------------------------------------------------------------- Winter */
  {
    id: 'snowflakes',
    name: 'Snowflakes',
    group: 'Winter',
    size: 68,
    body: [
      S('M34 12v44M15 23l38 22M53 23L15 45M34 20l-5-5M34 20l5-5M34 48l-5 5M34 48l5 5', 1.2),
      DOT(10, 58, 1.6, `${slate}55`),
      DOT(58, 12, 1.6, `${slate}55`),
    ].join(''),
  },
  {
    id: 'mittens',
    name: 'Mittens and cocoa',
    group: 'Winter',
    size: 76,
    body: [
      `<path d="M18 30h14a6 6 0 0 1 6 6v14a6 6 0 0 1-6 6H18a6 6 0 0 1-6-6V36a6 6 0 0 1 6-6z" fill="${rose}26" stroke="${rose}" stroke-width="1.3"/>`,
      S('M12 40c-4-4-8-2-8 2s4 6 8 4M12 36h26', 1.1),
      `<path d="M50 40h14v10a6 6 0 0 1-6 6h-2a6 6 0 0 1-6-6z" fill="${clay}2E" stroke="${rust}" stroke-width="1.2"/>`,
      S('M64 43h3a3 3 0 0 1 0 6h-3M54 34v-4M59 34v-4', 1.1),
    ].join(''),
  },
  {
    id: 'christmas',
    name: 'Christmas trees',
    group: 'Winter',
    size: 74,
    body: [
      `<path d="M36 14l12 16h-7l10 14h-8l9 12H20l9-12h-8l10-14h-7z" fill="${olive}26" stroke="${olive}" stroke-width="1.3"/>`,
      S('M36 56v8M31 64h10', 1.2),
      DOT(30, 40, 1.8, `${rose}77`),
      DOT(42, 48, 1.8, `${ochre}77`),
      DOT(36, 30, 1.8, `${rose}66`),
    ].join(''),
  },
  {
    id: 'candy-canes',
    name: 'Candy canes',
    group: 'Winter',
    size: 60,
    body: [
      `<path d="M24 52V26a8 8 0 0 1 16 0" fill="none" stroke="${rose}" stroke-width="4"/>`,
      `<path d="M24 52V26a8 8 0 0 1 16 0" fill="none" stroke="#F7F3ED" stroke-width="1.6" stroke-dasharray="4 6"/>`,
    ].join(''),
  },
  {
    id: 'winter-branches',
    name: 'Frosted branches',
    group: 'Winter',
    size: 80,
    body: [
      S('M4 68C24 56 40 36 48 12', 1.2),
      S('M20 58l-6-8M28 50l8-4M34 40l-8-6M40 30l9-3', 1),
      DOT(14, 50, 2, `${slate}44`),
      DOT(49, 27, 2, `${slate}44`),
    ].join(''),
  },

  /* --------------------------------------------------- Spring and summer */
  {
    id: 'tulips',
    name: 'Tulips',
    group: 'Spring and summer',
    size: 68,
    body: [
      `<path d="M26 30c0-6 4-10 8-10s8 4 8 10c0 6-3 10-8 10s-8-4-8-10z" fill="${rose}2E" stroke="${rose}" stroke-width="1.3"/>`,
      S('M34 20v10M28 24v8M40 24v8M34 40v22', 1.1),
      leaf(28, 52, 7, `${sage}3A`, 40),
      leaf(41, 48, 7, `${sage}3A`, -40),
    ].join(''),
  },
  {
    id: 'raindrops',
    name: 'April showers',
    group: 'Spring and summer',
    size: 54,
    body: [
      `<path d="M18 14c5 7 8 11 8 15a8 8 0 0 1-16 0c0-4 3-8 8-15z" fill="${slate}26" stroke="${slate}" stroke-width="1.2"/>`,
      `<path d="M40 34c3.5 5 5.5 8 5.5 10.5a5.5 5.5 0 0 1-11 0c0-2.5 2-5.5 5.5-10.5z" fill="${slate}1F" stroke="${slate}" stroke-width="1"/>`,
    ].join(''),
  },
  {
    id: 'watermelon',
    name: 'Watermelon',
    group: 'Spring and summer',
    size: 66,
    body: [
      `<path d="M12 24h40a20 20 0 0 1-40 0z" fill="${rose}2E" stroke="${rose}" stroke-width="1.3"/>`,
      S('M10 24h44', 1.6),
      DOT(24, 32, 1.6, `${rust}77`),
      DOT(32, 36, 1.6, `${rust}77`),
      DOT(40, 32, 1.6, `${rust}77`),
    ].join(''),
  },
  {
    id: 'seashells',
    name: 'Seashells',
    group: 'Spring and summer',
    size: 64,
    body: [
      `<path d="M32 46a16 16 0 0 1-16-16 16 16 0 0 1 32 0 16 16 0 0 1-16 16z" fill="${sand}44" stroke="${clay}" stroke-width="1.3"/>`,
      S('M32 46V30M32 46l-9-13M32 46l9-13M32 46l-14-9M32 46l14-9', 1),
    ].join(''),
  },
  {
    id: 'lemons',
    name: 'Lemonade',
    group: 'Spring and summer',
    size: 60,
    body: [
      `<ellipse cx="30" cy="30" rx="14" ry="10" fill="${ochre}2E" stroke="${clay}" stroke-width="1.3" transform="rotate(-25 30 30)"/>`,
      S('M18 36c2-3 6-4 8-3M44 24c-2 3-6 4-8 3', 1),
      leaf(44, 18, 6, `${sage}3A`, -30),
    ].join(''),
  },

  /* ------------------------------------------------------------ Holidays */
  {
    id: 'fourth-july',
    name: 'Fireworks',
    group: 'Holidays',
    size: 80,
    body: [
      S(
        'M40 40l0-16M40 40l0 16M40 40l-16 0M40 40l16 0M40 40l-11-11M40 40l11 11M40 40l11-11M40 40l-11 11',
        1.2,
      ),
      DOT(40, 20, 2, `${rose}66`),
      DOT(40, 60, 2, `${slate}66`),
      DOT(20, 40, 2, `${slate}66`),
      DOT(60, 40, 2, `${rose}66`),
      DOT(40, 40, 2.5, `${ochre}77`),
    ].join(''),
  },
  {
    id: 'valentines',
    name: "Valentine's",
    group: 'Holidays',
    size: 62,
    body: [
      FILL('M31 44S16 34 16 25.5A7 7 0 0 1 31 21a7 7 0 0 1 15 4.5C46 34 31 44 31 44z', `${rose}2E`),
      S('M31 44S16 34 16 25.5A7 7 0 0 1 31 21a7 7 0 0 1 15 4.5C46 34 31 44 31 44z', 1.2),
      S('M8 12l4 4M52 50l4 4', 1),
    ].join(''),
  },
  {
    id: 'shamrocks',
    name: 'Shamrocks',
    group: 'Holidays',
    size: 60,
    body: [
      leaf(30, 26, 7, `${olive}33`, 0),
      leaf(30, 26, 7, `${olive}33`, 120),
      leaf(30, 26, 7, `${olive}33`, 240),
      S('M30 30c0 10-3 14-8 18', 1.2),
    ].join(''),
  },
  {
    id: 'easter-eggs',
    name: 'Decorated eggs',
    group: 'Holidays',
    size: 64,
    body: [
      `<path d="M32 16c8 0 14 12 14 22a14 14 0 0 1-28 0c0-10 6-22 14-22z" fill="${mauve}26" stroke="${mauve}" stroke-width="1.3"/>`,
      S('M19 32h26M20 40h24', 1),
      S('M22 36c3-3 6-3 9 0s6 3 9 0', 1),
    ].join(''),
  },
  {
    id: 'birthday',
    name: 'Birthday cake',
    group: 'Holidays',
    size: 72,
    body: [
      `<path d="M16 42h40v18a4 4 0 0 1-4 4H20a4 4 0 0 1-4-4z" fill="${rose}22" stroke="${rose}" stroke-width="1.3"/>`,
      S('M16 50c5 4 10 4 13 0s8-4 13 0 9 4 14 0', 1.1),
      S('M28 42v-8M36 42v-10M44 42v-8', 1.2),
      DOT(28, 32, 1.8, `${ochre}88`),
      DOT(36, 30, 1.8, `${ochre}88`),
      DOT(44, 32, 1.8, `${ochre}88`),
    ].join(''),
  },
  {
    id: 'stars-stripes',
    name: 'Stars and stripes',
    group: 'Holidays',
    size: 70,
    body: [
      `<path d="M0 54h70M0 62h70" stroke="${rose}55" stroke-width="3"/>`,
      FILL(
        'M22 12l3.2 6.5 7.2 1-5.2 5.1 1.2 7.2-6.4-3.4-6.4 3.4 1.2-7.2-5.2-5.1 7.2-1z',
        `${slate}44`,
      ),
      FILL('M50 26l2.4 4.9 5.4.8-3.9 3.8.9 5.4-4.8-2.6-4.8 2.6.9-5.4-3.9-3.8 5.4-.8z', `${rose}44`),
    ].join(''),
  },
];

export function backgroundGroups(p: Palette = palette()): BackgroundGroup[] {
  return [...new Set(backgrounds(p).map((b) => b.group))];
}

export const DEFAULT_BACKGROUND = 'none';

export function backgroundById(
  id: string | null | undefined,
  p: Palette = palette(),
): Background | null {
  return backgrounds(p).find((b) => b.id === id) ?? null;
}

/**
 * The pattern as a CSS `background-image` value.
 *
 * `opacity` on the group rather than on the colours keeps every pattern equally
 * faint no matter how it was drawn — otherwise the busy ones shout and the
 * sparse ones vanish.
 */
export function backgroundImage(id: string | null | undefined, opacity = 0.5): string {
  const bg = backgroundById(id);
  if (!bg || !bg.body) return 'none';

  const alpha = bg.fullStrength ? 1 : opacity;

  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="${bg.size}" height="${bg.size}" viewBox="0 0 ${bg.size} ${bg.size}">` +
    `<g fill="none" stroke="${clay}" stroke-linecap="round" stroke-linejoin="round" opacity="${alpha}">${bg.body}</g>` +
    `</svg>`;

  return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
}

/** The page colour a background asks for, or the theme's own lightest. */
export function backgroundColor(id: string | null | undefined, p: Palette = palette()): string {
  return backgroundById(id, p)?.color ?? p.cream;
}

/* ------------------------------------------------------------- textures */

/**
 * A texture laid over whatever colour or pattern is underneath.
 *
 * Separate from the backgrounds rather than baked into them, because
 * fourteen colours times a dozen weaves is a picker nobody can read — and
 * because the two are genuinely different choices. Pick the paper, then pick
 * the grain.
 *
 * All drawn in one dark neutral and blended down to near-nothing, so the same
 * texture works over cream and over espresso without being redrawn per
 * colour. That is the trick that makes this composable at all.
 */
export interface Texture {
  id: string;
  name: string;
  size: number;
  body: string;
  /** Some weaves carry more ink than others and need reining in. */
  weight?: number;
}

/*
 * One ink for every texture, in every theme.
 *
 * Deliberately not the theme's own darkest: a texture is a shadow in the
 * paper, and at four per cent opacity the hue makes no visible difference
 * while a per-theme ink would mean regenerating twenty data URIs on every
 * theme change.
 */
const INK = '#241E18';

/** Shorthands, in the texture ink. */
const L = (d: string, w = 1) => `<path d="${d}" stroke="${INK}" stroke-width="${w}" fill="none"/>`;
const D = (cx: number, cy: number, r: number) =>
  `<circle cx="${cx}" cy="${cy}" r="${r}" fill="${INK}"/>`;

export const TEXTURES: Texture[] = [
  { id: 'none', name: 'None', size: 10, body: '' },

  { id: 'linen', name: 'Linen', size: 8, body: L('M0 4h8M4 0v8', 0.8) },
  {
    id: 'canvas',
    name: 'Canvas',
    size: 14,
    body: L('M0 7h14M7 0v14', 1.4) + L('M0 0h14M0 14h14', 0.6),
  },
  { id: 'burlap', name: 'Burlap', size: 6, body: L('M0 3h6M3 0v6', 1.2), weight: 1.3 },

  {
    id: 'grain',
    name: 'Paper grain',
    size: 22,
    body: [
      [3, 5],
      [11, 2],
      [18, 8],
      [7, 13],
      [15, 17],
      [2, 19],
      [20, 14],
      [9, 9],
    ]
      .map(([x, y]) => D(x!, y!, 0.7))
      .join(''),
    weight: 1.6,
  },

  {
    id: 'speckle',
    name: 'Speckle',
    size: 34,
    body: [
      [4, 6, 1.5],
      [17, 3, 0.9],
      [27, 11, 1.8],
      [9, 19, 1.1],
      [22, 24, 1.4],
      [31, 29, 0.9],
      [13, 30, 1.6],
      [2, 26, 0.8],
    ]
      .map(([x, y, r]) => D(x!, y!, r!))
      .join(''),
    weight: 1.2,
  },

  { id: 'pinstripe', name: 'Pinstripe', size: 10, body: L('M2 0v10', 0.9) },
  { id: 'ruled', name: 'Ruled', size: 12, body: L('M0 11h12', 0.9) },
  { id: 'grid', name: 'Fine grid', size: 16, body: L('M16 0H0v16', 0.7) },
  { id: 'hatch', name: 'Diagonal hatch', size: 10, body: L('M-2 8l10-10M4 14l10-10', 0.9) },
  {
    id: 'crosshatch',
    name: 'Crosshatch',
    size: 12,
    body: L('M-2 10l12-12M2 14l12-12', 0.7) + L('M-2 2l12 12M2 -2l12 12', 0.7),
  },

  {
    id: 'chevron',
    name: 'Chevron',
    size: 20,
    body: L('M0 14l10-8 10 8', 1.1) + L('M0 4l10-8 10 8', 1.1),
  },

  {
    id: 'herringbone',
    name: 'Herringbone',
    size: 20,
    body: L('M0 0l10 10M10 0L0 10M10 10l10 10M20 10l-10 10', 1),
  },

  {
    id: 'basketweave',
    name: 'Basket weave',
    size: 16,
    body:
      `<rect x="0" y="0" width="7" height="7" fill="none" stroke="${INK}" stroke-width="0.8"/>` +
      `<rect x="8" y="8" width="7" height="7" fill="none" stroke="${INK}" stroke-width="0.8"/>` +
      L('M0 1.5h7M0 3.5h7M0 5.5h7', 0.4) +
      L('M9.5 8v7M11.5 8v7M13.5 8v7', 0.4),
  },

  { id: 'dots', name: 'Dotted', size: 14, body: D(7, 7, 1.1) },
  { id: 'lattice', name: 'Diamond lattice', size: 18, body: L('M9 0l9 9-9 9-9-9z', 0.8) },
  {
    id: 'scallop',
    name: 'Scallops',
    size: 20,
    body: L('M0 14a10 10 0 0 1 20 0', 1) + L('M-10 14a10 10 0 0 1 20 0M10 14a10 10 0 0 1 20 0', 1),
  },
  { id: 'arches', name: 'Arches', size: 24, body: L('M2 24V12a10 10 0 0 1 20 0v12', 1) },
  { id: 'waves', name: 'Waves', size: 24, body: L('M0 12c4-6 8-6 12 0s8 6 12 0', 1) },
  {
    id: 'terrazzo',
    name: 'Terrazzo',
    size: 40,
    body: [
      `<rect x="5" y="7" width="5" height="3" rx="1.5" fill="${INK}" transform="rotate(24 7 8)"/>`,
      `<rect x="24" y="4" width="4" height="3" rx="1.5" fill="${INK}" transform="rotate(-40 26 5)"/>`,
      `<rect x="14" y="22" width="6" height="3" rx="1.5" fill="${INK}" transform="rotate(60 17 23)"/>`,
      `<rect x="31" y="27" width="5" height="3" rx="1.5" fill="${INK}" transform="rotate(-15 33 28)"/>`,
      `<rect x="4" y="33" width="4" height="3" rx="1.5" fill="${INK}" transform="rotate(35 6 34)"/>`,
      D(33, 14, 1.6),
      D(19, 12, 1.2),
    ].join(''),
    weight: 1.1,
  },
];

/**
 * How strong a texture is laid on.
 *
 * A number rather than a name in the stored setting, so the scale can be
 * retuned later without stranding anyone's choice.
 */
export const TEXTURE_STRENGTHS = [
  { value: 0.04, label: 'Barely there' },
  { value: 0.07, label: 'Subtle' },
  { value: 0.11, label: 'Medium' },
  { value: 0.16, label: 'Strong' },
] as const;

export const DEFAULT_TEXTURE_STRENGTH = 0.07;

export function textureById(id: string | null | undefined): Texture | null {
  return TEXTURES.find((t) => t.id === id) ?? null;
}

/** The texture as a CSS `background-image` value. */
export function textureImage(
  id: string | null | undefined,
  strength = DEFAULT_TEXTURE_STRENGTH,
): string {
  const texture = textureById(id);
  if (!texture || !texture.body) return 'none';

  const opacity = Math.min(strength * (texture.weight ?? 1), 0.4);

  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="${texture.size}" height="${texture.size}" viewBox="0 0 ${texture.size} ${texture.size}">` +
    `<g opacity="${opacity.toFixed(3)}" stroke-linecap="square">${texture.body}</g>` +
    `</svg>`;

  return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
}
